import { Form, InputNumber, Modal, Select } from "antd";
import { useIntl } from "react-intl";
import { COMPARISON_TYPE_OPTIONS } from "~pages/LeaderCriteria/constants/comparisonTypeSymbols.ts";
import { useGetStatisticFieldOptions } from "~pages/LeaderCriteria/hooks/useGetStatisticFieldOptions.ts";
import { useUpdateLeaderCriteriaRule } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/hooks/useUpdateLeaderCriteriaRule.ts";
import type { LeaderCriteriaRule } from "~pages/LeaderCriteria/types/leaderCriteriaRule.ts";
import type { LeaderCriteriaRuleModel } from "~pages/LeaderCriteria/types/leaderCriteriaRuleModel.ts";

type Props = {
  rule: LeaderCriteriaRule;
  onClose: () => void;
};

export const LeaderCriteriaRuleEditModal = ({ rule, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<LeaderCriteriaRuleModel>();
  const statisticFieldOptions = useGetStatisticFieldOptions();
  const { mutateAsync: updateLeaderCriteriaRule, isPending } = useUpdateLeaderCriteriaRule();

  const handleSubmit = async () => {
    const formValues = await form.validateFields();

    await updateLeaderCriteriaRule({ ...formValues, qualifierId: rule.qualifierId });

    onClose();
  };

  const requiredRule = (fieldIntlKey: string) => [
    {
      required: true,
      message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: fieldIntlKey }) }),
    },
  ];

  return (
    <Modal
      width={550}
      open={true}
      closable={true}
      destroyOnHidden
      onCancel={onClose}
      title={formatMessage({ id: "edit" })}
      okText={formatMessage({ id: "save" })}
      onOk={() => void handleSubmit()}
      okButtonProps={{ loading: isPending }}
      cancelText={formatMessage({ id: "cancel" })}
    >
      <Form
        form={form}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
        colon={false}
        labelWrap={true}
        initialValues={{
          statisticField: rule.statisticField,
          comparisonField: rule.comparisonField,
          comparisonType: rule.comparisonType,
          comparisonValue: rule.comparisonValue,
        }}
      >
        <Form.Item
          name="statisticField"
          label={formatMessage({ id: "statistic.field" })}
          tooltip={formatMessage({ id: "form.help.qualification.rule.statistic.field" })}
          rules={requiredRule("statistic.field")}
        >
          <Select showSearch optionFilterProp="label" options={statisticFieldOptions} />
        </Form.Item>
        <Form.Item
          name="comparisonField"
          label={formatMessage({ id: "comparison.field" })}
          tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.field" })}
          rules={requiredRule("comparison.field")}
        >
          <Select showSearch optionFilterProp="label" options={statisticFieldOptions} />
        </Form.Item>
        <Form.Item
          name="comparisonType"
          label={formatMessage({ id: "comparison.type" })}
          tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.type" })}
          rules={requiredRule("comparison.type")}
        >
          <Select options={COMPARISON_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="comparisonValue"
          label={formatMessage({ id: "comparison.value" })}
          tooltip={formatMessage({ id: "form.help.qualification.rule.comparison.value" })}
          rules={requiredRule("comparison.value")}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
